import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Modal } from "../../../../components/shared";

const MAX_CONCURRENCY = 4;
const MAX_FILE_SIZE_MB = 200;

export type FileUploadStatus = "pending" | "uploading" | "done" | "error";

export interface FileWithStatus {
  file: File;
  status: FileUploadStatus;
  error?: string;
}

interface LogUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  entryId: string;
  onUploadFiles: (entryId: string, files: File[]) => Promise<void>;
  onComplete: () => void;
}

function runWithConcurrency(
  files: File[],
  concurrency: number,
  uploadOne: (file: File) => Promise<void>,
  onFileStatus: (file: File, status: FileUploadStatus, error?: string) => void
): Promise<void> {
  let index = 0;
  let inFlight = 0;

  return new Promise((resolve) => {
    function runNext() {
      while (inFlight < concurrency && index < files.length) {
        const file = files[index++];
        inFlight++;
        onFileStatus(file, "uploading");
        uploadOne(file)
          .then(() => onFileStatus(file, "done"))
          .catch((err: Error) => onFileStatus(file, "error", err.message ?? "Falha no envio"))
          .finally(() => {
            inFlight--;
            if (index < files.length || inFlight > 0) runNext();
            else resolve();
          });
      }
    }
    runNext();
  });
}

export default function LogUploadModal({
  isOpen,
  onClose,
  entryId,
  onUploadFiles,
  onComplete,
}: LogUploadModalProps) {
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isOpen) setFiles([]);
  }, [isOpen]);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const list = Array.from(newFiles).filter(
      (f) => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024
    );
    setFiles((prev) => [
      ...prev,
      ...list.map((file) => ({ file, status: "pending" as FileUploadStatus })),
    ]);
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleStartUpload = useCallback(async () => {
    const pending = files.filter((f) => f.status === "pending").map((f) => f.file);
    if (pending.length === 0) {
      onComplete();
      onClose();
      return;
    }
    setUploading(true);

    const updateStatus = (file: File, status: FileUploadStatus, error?: string) => {
      setFiles((prev) =>
        prev.map((item) =>
          item.file === file ? { ...item, status, error } : item
        )
      );
    };

    try {
      await runWithConcurrency(
        pending,
        MAX_CONCURRENCY,
        (file) => onUploadFiles(entryId, [file]),
        updateStatus
      );
      onComplete();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  }, [entryId, files, onUploadFiles, onComplete, onClose]);

  const handleClose = useCallback(() => {
    if (!uploading) {
      setFiles([]);
      onClose();
    }
  }, [uploading, onClose]);

  const pendingCount = files.filter((f) => f.status === "pending").length;
  const canStart = pendingCount > 0 && !uploading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enviar documentos"
      maxWidth="max-w-2xl"
      closeOnOverlayClick={!uploading}
    >
      <div className="space-y-6">
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`relative border-2 border-dashed p-8 text-center transition-colors duration-200 ${
            isDragging
              ? "border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/50"
              : "border-primary-200 dark:border-primary-800 hover:border-primary-300 dark:hover:border-primary-700"
          }`}
        >
          <p className="text-sm text-primary-600 dark:text-primary-400 mb-2">
            Arraste arquivos aqui ou clique para selecionar
          </p>
          <p className="text-xs text-primary-500 dark:text-primary-500">
            Múltiplos arquivos • máx. {MAX_FILE_SIZE_MB} MiB por arquivo
          </p>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,image/*,.txt,.csv"
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = "";
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label="Selecionar arquivos"
          />
        </div>

        {files.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 mb-2">
              Arquivos ({files.length})
            </div>
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {files.map((item, index) => (
                <li
                  key={`${item.file.name}-${index}`}
                  className="flex items-center justify-between py-2 border-b border-primary-100 dark:border-primary-800 last:border-b-0 text-sm"
                >
                  <span className="truncate text-primary-800 dark:text-primary-200 min-w-0">
                    {item.file.name}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {item.status === "pending" && (
                      <span className="text-primary-500">na fila</span>
                    )}
                    {item.status === "uploading" && (
                      <span className="text-primary-600 dark:text-primary-400">
                        enviando...
                      </span>
                    )}
                    {item.status === "done" && (
                      <span className="text-green-600 dark:text-green-400">
                        enviado
                      </span>
                    )}
                    {item.status === "error" && (
                      <span className="text-red-600 dark:text-red-400" title={item.error}>
                        erro
                      </span>
                    )}
                    {item.status === "pending" && !uploading && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => removeFile(index)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 text-xs"
                      >
                        Remover
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            {uploading ? "ENVIANDO..." : "CANCELAR"}
          </Button>
          <Button
            type="button"
            onClick={handleStartUpload}
            disabled={!canStart}
          >
            ENVIAR {pendingCount > 0 ? `(${pendingCount})` : ""}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
